import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { ArrowLeftThin } from '@ori-ui/icons';
import { Button, ButtonBase, IconButton, Link, styled, Typography } from '@ori-ui/mui';
import { Formik, Form } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { BUSINESS_OPPORTUNITY_URL, DO_LOGIN_URL, EXTERNAL_ACCOUNT_URL, RETURN_URL_QUERY_PARAM_KEY, VALIDATE_LOGIN_URL } from '../../constants';
import { useConfigurationContext } from '../../contexts/ConfigurationContext';
import { getCustomAxios, handleAxiosError } from '../../helpers';
import { AccountPanes, SocialMediaProviders } from '../../models';
import { LocalizationKeys } from '../../models/localizationKeys';
import { Input } from './Input';
import { PasswordInput } from './PasswordInput';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var ConnectExternalAccountRoot = styled('div')(function (_ref) {
  var theme = _ref.theme;
  return {
    boxSizing: 'border-box',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body1.fontSize,
    height: '100%',
    width: '100%'
  };
});
var ConnectExternalAccountHeader = styled('div')(function (_ref2) {
  var theme = _ref2.theme;
  return {
    alignItems: 'center',
    display: 'flex',
    fontSize: theme.typography.h6.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    justifyContent: 'center',
    lineHeight: theme.typography.pxToRem(32),
    margin: theme.spacing(3, 0, 0, 2)
  };
});
var ConnectExternalAccountHeaderIcon = styled(IconButton)(function (_ref3) {
  var theme = _ref3.theme;
  return {
    fontSize: theme.typography.h4.fontSize,
    lineHeight: theme.typography.pxToRem(20),
    marginRight: theme.spacing(1)
  };
});
var ConnectExternalAccountInformation = styled(Typography)(function (_ref4) {
  var theme = _ref4.theme;
  return {
    color: theme.palette.text.secondary,
    lineHeight: theme.typography.pxToRem(19),
    margin: theme.spacing(2, 4)
  };
});
var ConnectExternalAccountForm = styled(Form)(function (_ref5) {
  var theme = _ref5.theme;
  return {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 4)
  };
});
var ConnectExternalAccountError = styled(Typography)(function (_ref6) {
  var theme = _ref6.theme;
  return {
    color: theme.palette.error.main,
    margin: theme.spacing(0, 2)
  };
});
var ConnectExternalAccountFormSubmit = styled(Button)(function (_ref7) {
  var theme = _ref7.theme;
  return {
    height: 40,
    marginTop: theme.spacing(1),
    padding: theme.spacing(0, 6)
  };
});
var ConnectExternalAccountForgottenPassword = styled(ButtonBase)(function (_ref8) {
  var theme = _ref8.theme;
  return {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: theme.typography.pxToRem(18),
    margin: theme.spacing(3, 'auto', 10),
    textDecoration: 'underline'
  };
});
var ConnectExternalAccountFormBrandPartner = styled('div')(function (_ref9) {
  var theme = _ref9.theme;
  return {
    color: theme.palette.text.primary,
    textAlign: 'center',
    width: '100%'
  };
});
var ConnectExternalAccountFormRegister = styled(Link)(function (_ref10) {
  var theme = _ref10.theme;
  return {
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    fontWeight: theme.typography.fontWeightBold,
    marginLeft: theme.spacing(2)
  };
});
export var ConnectExternalAccount = function ConnectExternalAccount(_ref11) {
  var goToPane = _ref11.goToPane;

  var _useTranslation = useTranslation(),
      _useTranslation2 = _slicedToArray(_useTranslation, 1),
      translate = _useTranslation2[0];

  var _useConfigurationCont = useConfigurationContext(),
      externalLoginName = _useConfigurationCont.externalLoginName,
      externalProviderType = _useConfigurationCont.externalProviderType,
      currentLanguage = _useConfigurationCont.currentLanguage;

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      errorMessage = _useState2[0],
      setErrorMessage = _useState2[1];

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      returnUrl = _useState4[0],
      setReturnUrl = _useState4[1];

  var formRef = useRef(null);
  var loginSchema = Yup.object().shape({
    Password: Yup.string().required(translate(LocalizationKeys.enterPassword.id)),
    Username: Yup.string().required(translate(LocalizationKeys.enterEmailOrBrandPartnerNumber.id))
  });
  var connectAccountsUrl = new URL(EXTERNAL_ACCOUNT_URL, window.location.origin);
  var validateLoginUrl = new URL(VALIDATE_LOGIN_URL, window.location.origin);
  var doLoginUrl = new URL(DO_LOGIN_URL, window.location.origin);
  var handleGoBackButtonClick = useCallback(function () {
    goToPane(AccountPanes.LoginPane);
  }, [goToPane]);
  var handleForgotPasswordinkClick = useCallback(function () {
    goToPane(AccountPanes.ForgotPassPane);
  }, [goToPane]);

  var onConnectAccountSubmit = function onConnectAccountSubmit() {
    return async function (values, actions) {
      actions.setSubmitting(false);

      try {
        var connectAccountValidateResponse = await getCustomAxios(currentLanguage).post(connectAccountsUrl.toString(), values);
        var loginValidateResponse = await getCustomAxios(currentLanguage).post(validateLoginUrl.toString(), {
          Username: values.Username,
          Password: values.Password
        });

        if (connectAccountValidateResponse.data.Success && loginValidateResponse.data.Success) {
          try {
            if (!formRef.current) {
              return;
            }

            formRef.current.submit();
          } catch (_unused) {
            // To consider more appropriate error message?
            setErrorMessage(translate(LocalizationKeys.errorOccured.id));
          }

          return;
        }
      } catch (error) {
        handleAxiosError({
          error: error,
          translateMessage: function translateMessage(key) {
            return setErrorMessage(translate(key));
          }
        });
      }
    };
  };

  useEffect(function () {
    var _URL = new URL(window.location.href),
        searchParams = _URL.searchParams;

    var url = searchParams.get(RETURN_URL_QUERY_PARAM_KEY);

    if (!url) {
      return;
    }

    setReturnUrl(url);
  }, []);
  return /*#__PURE__*/_jsxs(ConnectExternalAccountRoot, {
    children: [/*#__PURE__*/_jsxs(ConnectExternalAccountHeader, {
      children: [/*#__PURE__*/_jsx(ConnectExternalAccountHeaderIcon, {
        onClick: handleGoBackButtonClick,
        children: /*#__PURE__*/_jsx(ArrowLeftThin, {
          fontSize: "inherit"
        })
      }), /*#__PURE__*/_jsx(Typography, {
        sx: {
          width: 'calc(100% - 30px)'
        },
        component: "span",
        children: translate(LocalizationKeys.connectToYourAccount.id)
      })]
    }), /*#__PURE__*/_jsx(ConnectExternalAccountInformation, {
      children: translate(LocalizationKeys.theFirstTimeYouLogIn.id, {
        SocialNetwork: SocialMediaProviders[externalProviderType]
      })
    }), /*#__PURE__*/_jsx(Formik, {
      initialValues: {
        Username: '',
        Password: '',
        ExternalProviderType: SocialMediaProviders[externalProviderType],
        ExternalLoginName: externalLoginName
      },
      validationSchema: loginSchema,
      onSubmit: onConnectAccountSubmit(),
      children: /*#__PURE__*/_jsxs(ConnectExternalAccountForm, {
        ref: formRef,
        method: "POST",
        action: doLoginUrl.toString(),
        children: [errorMessage && /*#__PURE__*/_jsx(ConnectExternalAccountError, {
          children: errorMessage
        }), /*#__PURE__*/_jsx(Input, {
          autoComplete: "username",
          type: "text",
          name: "Username",
          label: "".concat(translate(LocalizationKeys.emailAddressOrRegistrationNumber.id), "*")
        }), /*#__PURE__*/_jsx(PasswordInput, {
          autoComplete: "current-password",
          name: "Password"
        }), /*#__PURE__*/_jsx("input", {
          hidden: true,
          readOnly: true,
          name: "ReturnUrl",
          value: returnUrl
        }), /*#__PURE__*/_jsx(ConnectExternalAccountFormSubmit, {
          disableRipple: true,
          color: "primary",
          variant: "contained",
          type: "submit",
          children: translate(LocalizationKeys.connect.id)
        }), /*#__PURE__*/_jsx(ConnectExternalAccountForgottenPassword, {
          disableRipple: true,
          onClick: handleForgotPasswordinkClick,
          children: translate(LocalizationKeys.forgotPassword.id)
        }), /*#__PURE__*/_jsxs(ConnectExternalAccountFormBrandPartner, {
          children: [translate(LocalizationKeys.notABrandPartnerYet.id), /*#__PURE__*/_jsx(ConnectExternalAccountFormRegister, {
            variant: "button",
            href: BUSINESS_OPPORTUNITY_URL,
            children: translate(LocalizationKeys.register.id)
          })]
        })]
      })
    })]
  });
};
export default ConnectExternalAccount;